<template>
  <div class="fpc">
    <div class="fpc-header">
      <div class="fpc-avatar" :style="avatarStyle">
        {{ initials }}
      </div>
      <div class="fpc-meta">
        <div class="fpc-author">{{ item.authorName }}</div>
        <div class="fpc-sub">
          <span v-if="item.groupName" class="fpc-group">{{ item.groupName }} · </span>
          {{ item.timeAgo || '' }}
        </div>
      </div>
      <span v-if="item.tag" class="fpc-tag" :class="'fpc-tag--' + (item.tagColor || 'green')">{{ item.tag }}</span>
      <span v-if="item.pinned" class="fpc-tag fpc-tag--gold fpc-pinned">
        <f7-icon material="pin" :size="12" /> {{ $t('feed.important') }}
      </span>
      <FeedCardMenu
        :can-manage="canManage"
        :pinned="!!item.pinned"
        @pin="$emit('pin', { item, pinned: $event })"
        @edit="$emit('edit', item)"
        @delete="$emit('delete', item)"
      />
    </div>

    <div v-if="item.headline" class="fpc-headline">{{ item.headline }}</div>
    <div v-if="item.text" class="fpc-text">
      <span v-html="truncatedText"></span>
      <f7-link v-if="item.text.length > textLimit" class="fpc-more" @click.stop="expanded = !expanded">
        {{ expanded ? '' : $t('feed.readMore') }}
      </f7-link>
    </div>

    <FeedMediaGrid v-if="item.media && item.media.length > 0" :urls="item.media" @open="openMedia" />

    <div v-if="item.campaignAttachment" class="fpc-campaign" :style="{ borderColor: campaignBorderColor }">
      <div class="fpc-campaign-header">
        <div class="fpc-campaign-icon" :style="{ background: campaignIconBg }">
          <f7-icon v-if="campaignIcon !== 'device_floppy_fill'" :f7="campaignIcon" :size="15" :style="{ color: campaignIconColor }" />
          <span v-else class="material-symbols-outlined" :style="{ color: campaignIconColor, fontSize: '15px' }">device_floppy_fill</span>
        </div>
        <div>
          <div class="fpc-campaign-name">{{ item.campaignAttachment.name }}</div>
          <div class="fpc-campaign-sub">{{ item.campaignAttachment.sub }}</div>
        </div>
      </div>
      <div v-if="item.campaignAttachment.progress" class="fpc-progress">
        <f7-progressbar class="fpc-progressbar" :progress="campaignPct" :style="{ '--f7-progressbar-height': '4px', '--f7-progressbar-bg-color': '#E5E7EB', '--f7-progressbar-color': campaignBorderColor }" />
        <div class="fpc-progress-footer">
          <span>{{ item.campaignAttachment.progressLabel }}</span>
          <span v-if="item.campaignAttachment.progressPct" :style="{ color: campaignBorderColor, fontWeight: 500 }">
            {{ item.campaignAttachment.progressPct }}
          </span>
        </div>
      </div>
    </div>

    <FeedReactionBar
      :reacted="userReacted"
      :reactionType="userReactionType"
      :reactionCount="totalReactions"
      :reactions="item.reactions || []"
      :commentsCount="item.commentsCount || 0"
      :repostsCount="repostsCount"
      :share-content="shareContent"
      @react="onReact"
      @comment="onCommentToggle"
      @repost="showRepostComposer = true"
      @shareToChat="showShareToChat = true"
    />

    <div v-if="showComments" class="fpc-comments">
      <FeedCommentThread
        :comments="item.comments || []"
        :current-user-id="currentUserId"
        @react-comment="onReactComment"
        @reply="onReply"
        @load-more-replies="$emit('load-more-replies', $event)"
      />
      <FeedCommentComposer
        :placeholder="$t('feed.writeComment')"
        class="fpc-comment-composer"
        @submit="onCommentSubmit"
      />
    </div>

    <ShareToChatSheet
      v-model:opened="showShareToChat"
      :groups="chatGroups"
      @selectGroup="onShareToChat"
    />

    <FeedRepostComposer
      v-model:opened="showRepostComposer"
      :item="item"
      :user-name="userName"
      :user-avatar="userAvatar"
      @submit="onRepostSubmit"
    />

    <FeedCommentsSheet
      v-model:opened="showCommentsSheet"
      :comments="item.comments || []"
      :comments-count="item.commentsCount || 0"
      :reactions="item.reactions || []"
      :reposts-count="repostsCount"
      :current-user-id="currentUserId"
      @comment="onCommentSubmit"
      @react-comment="onReactComment"
      @reply="onReply"
      @load-more-replies="$emit('load-more-replies', $event)"
    />
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import i18n from '../../js/i18n.js'
import { f7 } from 'framework7-vue'
import FeedReactionBar from './FeedReactionBar.vue'

const { t } = i18n.global
import FeedMediaGrid from './FeedMediaGrid.vue'
import FeedCommentThread from './FeedCommentThread.vue'
import FeedCommentComposer from './FeedCommentComposer.vue'
import FeedRepostComposer from './FeedRepostComposer.vue'
import FeedCommentsSheet from './FeedCommentsSheet.vue'
import FeedCardMenu from './FeedCardMenu.vue'
import ShareToChatSheet from '../tamtam/ShareToChatSheet.vue'
import { useSocialShare } from '../../js/composables/useSocialShare.js'
import { useLargeScreen } from '../../js/composables/useMediaQuery.js'

const props = defineProps({
  item: { type: Object, required: true },
  currentUserId: { type: String, default: '' },
  chatGroups: { type: Array, default: () => [] },
  userName: { type: String, default: '' },
  userAvatar: { type: String, default: '' },
  canManage: { type: Boolean, default: false },
})

const emit = defineEmits(['react', 'comment', 'repost', 'share', 'shareToChat', 'react-comment', 'reply', 'load-more-replies', 'edit', 'delete', 'pin'])

const expanded = ref(false)
const showComments = ref(false)
const showCommentsSheet = ref(false)
const showShareToChat = ref(false)
const showRepostComposer = ref(false)
const textLimit = 200
const isLargeScreen = useLargeScreen().matches
const shareContent = computed(() => ({
  subject: props.item.headline || props.item.text || '',
  message: props.item.text || props.item.headline || '',
  url: `${window.location.origin}/post/${props.item.id}`,
  files: [],
}))

const truncatedText = computed(() => {
  if (!props.item.text) return ''
  if (expanded.value || props.item.text.length <= textLimit) return props.item.text
  return props.item.text.substring(0, props.item.text.lastIndexOf(' ', textLimit))
})

const initials = computed(() => {
  const name = props.item.authorName || ''
  return name.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase()
})

const avatarStyle = computed(() => {
  const colors = ['#1A6B42', '#3B4FCA', '#C9A84C', '#C0440A', '#6B46C1']
  const hash = (props.item.authorName || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0)
  const bg = colors[hash % colors.length]
  return { background: bg, color: '#fff' }
})

const userReacted = computed(() => {
  if (!props.item.reactions || !props.currentUserId) return false
  return props.item.reactions.some(r => r.users?.includes(props.currentUserId))
})

const userReactionType = computed(() => {
  if (!props.item.reactions || !props.currentUserId) return 'LIKE'
  const r = props.item.reactions.find(r => r.users?.includes(props.currentUserId))
  return r?.reactionType || 'LIKE'
})

const totalReactions = computed(() => {
  if (!props.item.reactions) return 0
  return props.item.reactions.reduce((sum, r) => sum + (r.count || 0), 0)
})

const repostsCount = computed(() => {
  if (!props.item.reactions) return 0
  const r = props.item.reactions.find(r => r.reactionType === 'Repost')
  return r?.count || 0
})

const campaignPct = computed(() => {
  const p = props.item.campaignAttachment?.progress
  if (!p) return 0
  return Math.min(100, Math.round((p.current / p.total) * 100))
})

const campaignBorderColor = computed(() => {
  const type = props.item.campaignAttachment?.type
  const map = { FUND_COLLECT: '#97C459', GROUP_SAVINGS: '#BA7517', TONTINE: '#3B4FCA', GROUP_BUY: '#3B4FCA' }
  return map[type] || '#1A6B42'
})

const campaignIcon = computed(() => {
  const type = props.item.campaignAttachment?.type
  const map = { FUND_COLLECT: 'gift_fill', GROUP_SAVINGS: 'device_floppy_fill', TONTINE: 'arrow_2_circlepath', GROUP_BUY: 'bag_fill' }
  return map[type] || 'star_fill'
})

const campaignIconBg = computed(() => {
  const type = props.item.campaignAttachment?.type
  const map = { FUND_COLLECT: '#EAF3DE', GROUP_SAVINGS: '#FAEEDA', TONTINE: '#EBF0FC', GROUP_BUY: '#EBF0FC' }
  return map[type] || '#EAF2EB'
})

const campaignIconColor = computed(() => {
  const type = props.item.campaignAttachment?.type
  const map = { FUND_COLLECT: '#27500A', GROUP_SAVINGS: '#633806', TONTINE: '#0C447C', GROUP_BUY: '#0C447C' }
  return map[type] || '#1A6B42'
})

function onReact(reactionType) {
  emit('react', { itemId: props.item.id, reactionType })
}

function onCommentToggle() {
  if (isLargeScreen.value) {
    showComments.value = !showComments.value
  } else {
    showComments.value = false
    showCommentsSheet.value = true
  }
}

function onRepostSubmit({ postId, text }) {
  emit('repost', { itemId: postId, text })
}

function onReactComment({ commentId, replyId, reactionType }) {
  emit('react-comment', { postId: props.item.id, commentId, replyId, reactionType })
}

function onReply({ commentId, replyId, text, attachments, replyTo }) {
  emit('reply', { postId: props.item.id, commentId, replyId, text, attachments, replyTo })
}

function onCommentSubmit({ text, attachments }) {
  emit('comment', { postId: props.item.id, text, attachments })
}

function openMedia(index) {
  if (props.item.media && props.item.media.length > 0) {
    f7.photoBrowser.open({
      photos: props.item.media.map(url => ({ url })),
      initialSlide: index,
    })
  }
}

function onShareToChat(group) {
  emit('shareToChat', { group, feedItem: props.item })
}
</script>

<style scoped>
.fpc {
  background: var(--f7-page-bg-color, #fff);
  border-radius: 10px;
  border: 0.5px solid var(--f7-list-item-border-color, var(--f7-list-item-border-color, #e0e0e0));
  padding: 12px 14px;
  margin-bottom: 10px;
  content-visibility: auto;
  contain-intrinsic-size: 280px;
}
.fpc-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 7px;
}
.fpc-avatar {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 500;
  flex-shrink: 0;
}
.fpc-meta { flex: 1; min-width: 0; }
.fpc-author { font-size: 13px; font-weight: 500; }
.fpc-sub { font-size: 12px; color: var(--f7-on-surface-variant, #8e8e93); }
.fpc-group { font-weight: 500; }
.fpc-tag {
  font-size: 11px;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 8px;
  flex-shrink: 0;
}
.fpc-tag--green { background: #EAF3DE; color: #27500A; }
.fpc-tag--blue { background: #EBF0FC; color: #0C447C; }
.fpc-tag--gold { background: #FDF6E3; color: #92400E; }
.fpc-tag--purple { background: #F5F0FF; color: #6B46C1; }
.fpc-tag--leaf { background: #EAF2EB; color: #3B6D11; }
.fpc-pinned {
  display: inline-flex;
  align-items: center;
  gap: 3px;
}
.fpc-headline {
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 4px;
}
.fpc-text {
  font-size: 13px;
  line-height: 1.55;
  color: var(--f7-text-color, #000);
  margin-bottom: 6px;
}
.fpc-more {
  font-size: 12px;
  color: var(--f7-on-surface-variant, #8e8e93);
  --f7-link-highlight-opacity: 0;
}
.fpc-campaign {
  border-radius: 8px;
  padding: 9px 10px;
  margin-bottom: 8px;
  border: 0.5px solid;
  background: var(--f7-list-bg-color, #f9f9f9);
}
.fpc-campaign-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}
.fpc-campaign-icon {
  width: 32px;
  height: 32px;
  border-radius: 7px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.fpc-campaign-name { font-size: 12px; font-weight: 500; }
.fpc-campaign-sub { font-size: 11px; color: var(--f7-on-surface-variant, #8e8e93); }
.fpc-progress { margin-top: 4px; }
.fpc-progress-footer {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  color: var(--f7-on-surface-variant, #8e8e93);
}
.fpc-comments {
  margin-top: 6px;
  border-top: 0.5px solid var(--f7-list-item-border-color, #e0e0e0);
  padding-top: 6px;
}
.fpc-comment-composer {
  margin-top: 8px;
}
</style>
